import {
	Form,
	FormItem,
	ReorderableList,
	Select,
	SelectItem,
	TextInput,
	ValidationError
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import {
	AddTilesResponseModel,
	AfasUpdateTileModel,
	UpdateAfasTabModel,
	AfasDetailsTileModel,
	updateAfasTile
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import { IObjectValidatorBuilder } from "client/Validation/builder";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { newGuid } from "client_legacy/ndaw/guid";
import { AfasResourceKind } from "client/Api/Workspace/LiveInformation/Afas/afasLiveInformation";
import { ExistingTile } from "./common";
import { EditTileFromData, initialGenericState } from "./EditGenericTile";
import { EditTileHandle } from "./EditTile";
import { EditTileGeneralFields } from "./GeneralFields";
import { useUpdateTile } from "./hooks/useUpdateTile";

const MAX_TAB_NUMBER = 20;

type AfasEditFormData = EditTileFromData<AfasUpdateTileModel>;

const validationBuilder: IValidationConfiguration<AfasEditFormData> =
	createValidationConfiguration<AfasEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

const tabValidation: IObjectValidatorBuilder<UpdateAfasTabModel> = validationBuilder
	.when(
		(formData): boolean =>	{
			const { columnSpan, rowSpan } = formData.dimension;
			return computeAreTabNeeded(columnSpan, rowSpan);
		}
	)
	.forCollection("tabs")
	.forItems(tab => tab.uniqueId);

tabValidation.forScalar("name")
	.add(
		isNotNullNorUndefined()
			.validateOn("anyInputFinished")
			.fixErrorOn("anyInput"),
		isNotEmpty()
			.validateOn("anyInputFinished")
			.fixErrorOn("anyInput")
	);

tabValidation.forScalar("resourceKind")
	.add(
		isNotNullNorUndefined()
			.validateOn("anyInputFinished")
			.fixErrorOn("anyInput"),
	);

const tabKindOptions: readonly SelectItem<AfasResourceKind>[] = [
	{ text: localize("_MyTasks"), value: AfasResourceKind.MyTasks },
	{ text: localize("_MySignals"), value: AfasResourceKind.MySignals }
];

export type EditAfasTileProps = {
	initialModel: AfasDetailsTileModel;
	mode: ExistingTile;
	onIsLoadingChange(isLoading: boolean): void;
	isLoading: boolean;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditAfasTile = forwardRef((props: EditAfasTileProps, ref: Ref<EditTileHandle>): ReactElement => {
	const {
		initialModel,
		mode,
		onIsLoadingChange,
		isLoading,
		onAfterSave,
	} = props;

	const [formData, setFormData] = useState<AfasEditFormData>({
		...initialGenericState(initialModel),
		destinationWindow: initialModel.destinationWindow,
		tabs: initialModel.tabs
	});
	const formHandler: IFormHandler<AfasEditFormData> = useFormHandler(
		formData,
		setFormData,
		validationBuilder
	);
	const { columnSpan, rowSpan } = formHandler.for("dimension")
		.value;

	const areTabsNeeded: boolean = useMemo(
		(): boolean => computeAreTabNeeded(columnSpan, rowSpan),
		[columnSpan, rowSpan]
	);

	const { updateHandler } = useUpdateTile<AfasUpdateTileModel>(
		formHandler,
		updateAfasTile,
		isLoading,
		onIsLoadingChange,
		mode,
		onAfterSave,
		prepareModelForSaving
	);

	useImperativeHandle(
		ref,
		() => ({
			updateHandler
		}),
		[updateHandler]
	);

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			setFormData(
				(formState) => {
					const tabsCopy: UpdateAfasTabModel[] = [...formState.tabs];
					const item: UpdateAfasTabModel = tabsCopy.splice(fromIndex, 1)[0];
					tabsCopy.splice(toIndex, 0, item);

					return {
						...formState,
						tabs: tabsCopy
					};
				}
			);
		},
		[]
	);

	const addTabHandler = (): void => {
		const newTab: UpdateAfasTabModel = {
			name: "",
			resourceKind: undefined,
			uniqueId: newGuid()
		};
		const tabsHandler: IValueInputAttachable<UpdateAfasTabModel[]> = formHandler.for("tabs");
		tabsHandler.reducer.push(newTab);
		tabsHandler.validation.triggerInputFinished();
	};

	const removeTabHandler = (index: number): void => {
		if (formHandler.for("tabs").value.length > 1) {
			formHandler.for("tabs").reducer.removeAt(index);
		}
	};

	return (
		<Form
			onSubmit="NONE"
		>
			<EditTileGeneralFields
				initialModel={initialModel}
				formHandler={formHandler}
			/>
			<AppDestinationWindowSelectFormItem
				attachable={formHandler.for("destinationWindow")}
			/>
			{areTabsNeeded && (
				<>
					<FormItem
						hasNoMargin
					>
						<ReorderableList onItemMove={itemMoveHandler}>
							{formHandler.for("tabs").map((tabHandler, index) => (
								<ReorderableList.PanelItem
									key={tabHandler.value.uniqueId}
									header={
										<Select
											{...tabHandler.for("resourceKind").attachToSelect()}
											options={tabKindOptions}
											menuTitle={localize("_Source")}
										/>
									}
									onRemove={() => removeTabHandler(index)}
								>
									<ValidationError {...tabHandler.for("resourceKind").attachToValidationError()} />
									<FormItem
										label={localize("_Content")}
									>
										<TextInput {...tabHandler.for("name").attachToTextInput()} />
										<ValidationError {...tabHandler.for("name").attachToValidationError()} />
									</FormItem>
								</ReorderableList.PanelItem>
							))}
						</ReorderableList>
						<ValidationError {...formHandler.for("tabs").attachToValidationError()} />
						{formData.tabs.length < MAX_TAB_NUMBER && (
							<ReorderableList.AddButton
								trackingId="add-tab-button"
								onClick={addTabHandler}
							/>
						)}
					</FormItem>
				</>
			)}
		</Form>
	);
});
EditAfasTile.displayName = "EditAfasTile";

function computeAreTabNeeded(columnSpan: number, rowSpan: number): boolean {
	return columnSpan >= 4 && rowSpan >= 4;
}

function prepareModelForSaving(formData: AfasEditFormData): AfasEditFormData {
	const { columnSpan, rowSpan } = formData.dimension;
	const areTabsNeeded = computeAreTabNeeded(columnSpan, rowSpan);

	return {
		...formData,
		tabs: areTabsNeeded
			? formData.tabs
			: []
	};
}